import { useEffect, useState } from "react";
import "./FilterPopup.css";
import { PopupItems } from "./constFilterPopupItems";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Button, TextField, Typography } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import rootStores from "../../stores";
import { toJS } from "mobx";
import { ISort } from "../../interfaces/Sort";
import dayjs from "dayjs";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { getAllEventsByClub } from "../../fetchers/RFIDTickets";

const RFIDTicketsStore = rootStores.RFIDTicketsStore;

type Props = {
  open?: boolean;
  handleClose: () => void;
  onClickFilter: () => void;
  sortData: ISort;
  onFieldChanged: (key: keyof ISort, value: any) => void;
  filterFields: string[];
};

interface IPopupItems {
  label: string;
  name: string;
  component: string;
  options?: any;
}

function FilterPopup(props: Props) {
  const { t, i18n } = useTranslation();
  const isLTR = i18n.language === "he";

  const { handleClose, onClickFilter, sortData, onFieldChanged, filterFields } = props;

  useEffect(() => {
    const clubPrefix = RFIDTicketsStore.club?.clubPrefix;
  
    if (!clubPrefix || clubPrefix === "ADMIN") {
      RFIDTicketsStore.setEvents([]);
      return;
    }
  
    getAllEventsByClub(clubPrefix)
      .then(events => RFIDTicketsStore.setEvents(events))
      .catch(console.error);
  }, [RFIDTicketsStore.club?.clubPrefix]);

  // FILTER SELECTORS
  const [stadium, setStadium] = useState({
    stadiumCode: "SHF_STADIUM_29",
    name: "טרנר באר שבע",
  });

  const formatOptions = (options: any) => {
    if (!Array.isArray(options)) return [];
    if (!sortData.clubPrefix) return options;
    return options.filter((opt: any) => opt.clubPrefix === sortData.clubPrefix);
  };

  const getOptions = (name: string) => {
    switch (name) {
      case "stadium":
        return [
          {
            stadiumCode: "SHF_STADIUM_29",
            name: "טרנר באר שבע",
          },
        ];
      case "area":
        return toJS(RFIDTicketsStore.areas);
      case "priceBand":
        return toJS(RFIDTicketsStore.priceBands);
      case "event":
        return Array.isArray(RFIDTicketsStore.events) ? toJS(RFIDTicketsStore.events) : [];
      default:
        return [];
    }
  };

  const getFormComponent = (item: IPopupItems) => {
    const options = getOptions(item.name);

    switch (item.component) {
      case filterFields.includes("orderDate") && "date":
        return (
          <div className="date-container" style={{ direction: isLTR ? "ltr" : "rtl" }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                slotProps={{ textField: { size: "small" } }}
                label={t("filter.fromDate")}
                format="dd/MM/yyyy"
                value={sortData?.fromDate ? dayjs(sortData.fromDate).toDate() : null}
                onChange={(fromDate: any) => onFieldChanged("fromDate", dayjs(fromDate).format("YYYY-MM-DD"))}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                slotProps={{ textField: { size: "small" } }}
                label={t("filter.toDate")}
                format="dd/MM/yyyy"
                value={sortData?.toDate ? dayjs(sortData.toDate).toDate() : null}
                minDate={sortData.fromDate ? dayjs(sortData.fromDate).toDate() : undefined}
                onChange={(toDate: any) => onFieldChanged("toDate", dayjs(toDate).format("YYYY-MM-DD"))}
              />
            </LocalizationProvider>
          </div>
        );

      case "select":
        switch (item.name) {
          case filterFields.includes("stadium") && "stadium":
            return (
              <Autocomplete
                size="small"
                options={options}
                getOptionLabel={(option: any) => option?.name}
                value={stadium}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(item.label)}
                    inputProps={{ ...params.inputProps, style: { textAlign: isLTR ? "left" : "right" } }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.stadiumCode === value?.stadiumCode}
                onChange={(event: any, newValue: any) => {
                  setStadium(newValue);
                  onFieldChanged("stadium", newValue.stadiumCode);
                }}
              />
            );

          case filterFields.includes("area") && "area":
            return (
              <Autocomplete
                size="small"
                options={formatOptions(options)}
                getOptionLabel={(option: any) => `${option.name} ${option.clubPrefix}-${option.id}`}
                value={sortData.area}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(item.label)}
                    inputProps={{ ...params.inputProps, style: { textAlign: isLTR ? "left" : "right" } }}
                  />
                )}
                onChange={(event: any, newValue: any) => onFieldChanged("area", newValue)}
              />
            );

          case filterFields.includes("priceBand") && "priceBand":
            return (
              <Autocomplete
                size="small"
                options={formatOptions(options)}
                getOptionLabel={(option: any) => option?.name + " " + option?.clubPrefix}
                value={sortData.priceBand}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(item.label)}
                    inputProps={{ ...params.inputProps, style: { textAlign: isLTR ? "left" : "right" } }}
                  />
                )}
                onChange={(event: any, newValue: any) => onFieldChanged("priceBand", newValue)}
              />
            );

          case filterFields.includes("event") && "event":
            return (
              <Autocomplete
                size="small"
                options={formatOptions(options)}
                getOptionLabel={(option: any) => option?.eventTitle}
                value={sortData.event}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t(item.label)}
                    inputProps={{ ...params.inputProps, style: { textAlign: isLTR ? "left" : "right" } }}
                  />
                )}
                onChange={(event: any, newValue: any) => onFieldChanged("event", newValue)}
              />
            );

          default:
            return null;
        }

      default:
        return null;
    }
  };

  const onClearFilter = () => {
    RFIDTicketsStore.clearFilter();
  };

  return (
    <div className="filter-container" style={{ direction: isLTR ? "ltr" : "rtl" }}>
      <div className="filter-title-container">
        <Typography className="filter-title" variant="h6" gutterBottom>
          {t("filter.title")}
        </Typography>
        <div className="filter-close">
          <Button sx={{ padding: "5px", minWidth: 0 }} onClick={handleClose}>
            <CloseRoundedIcon color="action" />
          </Button>
        </div>
      </div>

      <div className="filter-items-container">
        {PopupItems.map((item, index) => {
          if (item.name === "stadium") return <div key={index}></div>;
          if (filterFields.includes(item.name)) {
            return (
              <div className="filter-item" key={index}>
                <div className="filter-item-title" style={{ textAlign: isLTR ? "left" : "right" }}>
                  {t(item.label)}
                </div>
                <div className="filter-item-component">{getFormComponent(item)}</div>
              </div>
            );
          }
        })}
      </div>

      <div className="filter-btns-container">
        <Button className="btn" onClick={onClickFilter}>
          <span className="show">{t("filter.showResults")}</span>
        </Button>
        <Button className="btn" onClick={onClearFilter}>
          <span className="clean">{t("filter.clearAll")}</span>
        </Button>
      </div>
    </div>
  );
}

export default observer(FilterPopup);
